import { analytics } from '../../services/analytics';

import { GEO_RESTRICTION_DIALOG_TYPE } from './constants';

type RestrictionValue = 'social' | 'no_sc_purchase' | 'ticket' | 'allowed';
type GpsFailureReason = 'gps_permission_denied' | 'gps_unavailable' | 'timeout' | 'unknown';
type ProceedButtonText = 'play now' | 'play social only' | 'got it';

const RESTRICTION_VALUE: Record<GEO_RESTRICTION_DIALOG_TYPE, RestrictionValue> = {
  [GEO_RESTRICTION_DIALOG_TYPE.SOCIAL]: 'social',
  [GEO_RESTRICTION_DIALOG_TYPE.NO_SC_PURCHASE]: 'no_sc_purchase',
  [GEO_RESTRICTION_DIALOG_TYPE.TICKET]: 'ticket',
};

// ENABLE GPS reloads the page so the server re-resolves the restriction with GPS coords; the outcome is only
// known on the next boot, so the tap leaves this marker behind for GeoRestrictionStore to report gps_result.
const GPS_RESULT_PENDING_KEY = 'geo_restriction_gps_result_pending';

const baseProps = () => ({
  event_parent: 'geo' as const,
  event_category: 'location_notice' as const,
  page_url: window.location.href,
});

export const toRestrictionValue = (type: GEO_RESTRICTION_DIALOG_TYPE | undefined): RestrictionValue =>
  type ? RESTRICTION_VALUE[type] : 'allowed';

export const trackLocationNoticeImpression = (type: GEO_RESTRICTION_DIALOG_TYPE, triggeredBy: 'login' | 'signup') => {
  void analytics.sendGeoEvent('location_notice', {
    ...baseProps(),
    event_action: 'impression',
    event_type: 'restriction_type',
    event_value: toRestrictionValue(type),
    triggered_by: triggeredBy,
  });
};

export const trackEnableGps = () => {
  void analytics.sendGeoEvent('enable_gps', { ...baseProps(), event_action: 'click', button_text: 'enable gps' });
};

export const trackGpsResultSuccess = (type: GEO_RESTRICTION_DIALOG_TYPE | undefined) => {
  void analytics.sendGeoEvent('gps_result', {
    ...baseProps(),
    event_action: 'impression',
    status: 'success',
    event_type: 'restriction_type',
    event_value: toRestrictionValue(type),
  });
};

export const trackGpsResultFailure = (errorReason: GpsFailureReason, errorMessage: string) => {
  void analytics.sendGeoEvent('gps_result', {
    ...baseProps(),
    event_action: 'impression',
    status: 'failure',
    error_reason: errorReason,
    error_message: errorMessage,
  });
};

// The native permission prompt is still open after the position request timed out — the user hasn't answered.
export const trackGpsResultPending = () => {
  void analytics.sendGeoEvent('gps_result', { ...baseProps(), event_action: 'impression', status: 'pending' });
};

export const trackProceed = (buttonText: ProceedButtonText) => {
  void analytics.sendGeoEvent('proceed', { ...baseProps(), event_action: 'click', button_text: buttonText });
};

export const trackContactSupport = () => {
  void analytics.sendGeoEvent('contact_support', {
    ...baseProps(),
    event_action: 'click',
    button_text: 'contact support',
  });
};

export const trackLocationNoticeClose = () => {
  void analytics.sendGeoEvent('location_notice_close', { ...baseProps(), event_action: 'click' });
};

export const markGpsResultPending = () => {
  try {
    sessionStorage.setItem(GPS_RESULT_PENDING_KEY, '1');
  } catch {
    // Storage blocked: the gps_result for this tap is lost, nothing else depends on it.
  }
};

/** Reads and clears the marker left by an ENABLE GPS tap before the reload. */
export const takeGpsResultPending = (): boolean => {
  try {
    const isPending = sessionStorage.getItem(GPS_RESULT_PENDING_KEY) === '1';
    sessionStorage.removeItem(GPS_RESULT_PENDING_KEY);
    return isPending;
  } catch {
    return false;
  }
};
